import { useState, useEffect } from 'react'
import { Loader2, CheckCircle, ImageIcon } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { api } from '@/api/client'
import { cn } from '@/lib/utils'

interface MediaFolder {
  id: string
  name: string
  images: { id: string; url: string; name?: string }[]
}

interface MediaPickerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (url: string) => void
  multiple?: boolean
  onSelectMultiple?: (urls: string[]) => void
}

export function MediaPickerModal({ open, onOpenChange, onSelect, multiple, onSelectMultiple }: MediaPickerModalProps) {
  const [folders, setFolders] = useState<MediaFolder[]>([])
  const [loading, setLoading] = useState(false)
  const [activeFolder, setActiveFolder] = useState<string | null>(null)
  const [selected, setSelected] = useState<string[]>([])

  useEffect(() => {
    if (open) {
      setSelected([])
      setLoading(true)
      api.get<MediaFolder[]>('/media-folders')
        .then((data) => {
          setFolders(data || [])
          if (data?.length) setActiveFolder(data[0].id)
        })
        .catch(() => {})
        .finally(() => setLoading(false))
    }
  }, [open])

  const folder = folders.find((f) => f.id === activeFolder)
  const images = folder?.images.filter((img) => !img.url.match(/\.(mp4|mov|webm)$/i)) || []

  const toggleImage = (url: string) => {
    if (multiple) {
      setSelected((prev) => (prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]))
    } else {
      onSelect(url)
      onOpenChange(false)
    }
  }

  const confirmMultiple = () => {
    if (onSelectMultiple) onSelectMultiple(selected)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border shrink-0">
          <DialogTitle className="text-sm">Selecionar da Biblioteca</DialogTitle>
          {multiple && selected.length > 0 && (
            <p className="text-[11px] text-primary mt-0.5">{selected.length} selecionada(s)</p>
          )}
        </DialogHeader>

        {/* Folder tabs */}
        {!loading && folders.length > 0 && (
          <div className="flex gap-1.5 px-5 py-2.5 overflow-x-auto shrink-0 border-b border-border">
            {folders.map((f) => (
              <button
                key={f.id}
                onClick={() => setActiveFolder(f.id)}
                className={cn(
                  'px-2.5 py-1 rounded-lg text-[11px] font-medium whitespace-nowrap transition-all shrink-0',
                  activeFolder === f.id ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                )}
              >
                {f.name} <span className="opacity-60 ml-0.5">({f.images.filter((i) => !i.url.match(/\.(mp4|mov|webm)$/i)).length})</span>
              </button>
            ))}
          </div>
        )}

        {/* Image grid */}
        <div className="flex-1 overflow-y-auto p-4 min-h-[200px]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : images.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32">
              <ImageIcon className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Nenhuma imagem nesta pasta</p>
            </div>
          ) : (
            <div className="grid grid-cols-5 gap-2">
              {images.map((img) => {
                const isSelected = selected.includes(img.url)
                return (
                  <button
                    key={img.id}
                    onClick={() => toggleImage(img.url)}
                    className={cn(
                      'relative aspect-square rounded-xl overflow-hidden border-2 transition-all',
                      isSelected ? 'border-primary' : 'border-transparent hover:border-border'
                    )}
                  >
                    <img src={img.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    {isSelected && (
                      <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                          <CheckCircle className="h-4 w-4 text-primary-foreground" />
                        </div>
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer (only for multiple mode) */}
        {multiple && (
          <div className="px-5 py-3 border-t border-border flex items-center justify-between shrink-0">
            <button onClick={() => onOpenChange(false)} className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground">Cancelar</button>
            <button onClick={confirmMultiple} disabled={selected.length === 0} className="px-4 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50">
              Adicionar ({selected.length})
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
